import { cn } from "@core-ui/utils/cn";
import InlineMenuGroup, { type InlineMenuGroupButtons } from "@ext/markdown/core/edit/components/Menu/Groups/Inline";
import ListMenuGroup, { type ListMenuGroupButtons } from "@ext/markdown/core/edit/components/Menu/Groups/List";
import type { TableMenuGroupButtons } from "@ext/markdown/core/edit/components/Menu/Groups/Table";
import TableMenuGroup from "@ext/markdown/core/edit/components/Menu/Groups/Table";
import TableAggregation from "@ext/markdown/core/edit/components/Menu/Groups/TableAggregation";
import TextMenuGroup, { type TextMenuGroupButtons } from "@ext/markdown/core/edit/components/Menu/Groups/Text";
import type { InlineToolbarOptions } from "@ext/markdown/elements/article/edit/helpers/InlineToolbar";
import FileMenu from "@ext/markdown/elements/file/edit/components/FileMenu";
import type { Editor } from "@tiptap/core";
import { GlassToolbar, GlassToolbarSeparator } from "@ui-kit/GlassToolbar";
import { ComponentVariantProvider } from "@ui-kit/Providers";
import { memo, useCallback, useSyncExternalStore } from "react";
import { BlockActionsGroup } from "./BlockActionsGroup";
import { resolveToolbarContext, type ToolbarContext } from "./resolveToolbarContext";

export interface InlineToolbarButtons {
	tableGroup?: TableMenuGroupButtons & {
		aggregation?: boolean;
	};
	textGroup?: TextMenuGroupButtons;
	listGroup?: ListMenuGroupButtons;
	inlineGroup?: InlineMenuGroupButtons;
}

interface InlineEditPanelProps extends InlineToolbarOptions {
	editor: Editor | null;
	closeHandler?: () => void;
	buttons?: InlineToolbarButtons;
	className?: string;
	isMobile?: boolean;
	toolbarContext?: ToolbarContext;
}

const InlineEditPanel = memo((props: InlineEditPanelProps) => {
	const { editor, closeHandler, isInTable, isCellSelection, buttons, className, isMobile, toolbarContext } = props;
	const subscribe = useCallback(
		(listener: () => void) => {
			if (!editor || editor.isDestroyed || toolbarContext) return () => {};
			editor.on("transaction", listener);
			return () => {
				editor.off("transaction", listener);
			};
		},
		[editor, toolbarContext],
	);
	const getSnapshot = useCallback(() => {
		if (!editor || editor.isDestroyed) return null;
		return editor.state;
	}, [editor]);
	const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
	if (!state) return null;
	const context = toolbarContext ?? resolveToolbarContext(state);
	const {
		tableGroup = { aggregation: true, mergeCells: true, splitCells: true, deleteRow: true, deleteColumn: true },
		textGroup = {},
		listGroup = {},
		inlineGroup = {},
	} = buttons || {};

	return (
		<ComponentVariantProvider variant="glass">
			<GlassToolbar
				className={cn(
					className,
					context.kind === "file" && !isMobile && "w-[18.75rem] max-w-[calc(100vw-1rem)]",
				)}
				data-qa="qa-inline-wysiwyg-menu"
				role={isMobile ? "toolbar" : "article-inline-toolbar"}
			>
				{context.kind === "file" ? (
					<FileMenu context={context} editor={editor} />
				) : context.kind !== "text" ? (
					<>
						{context.kind === "node" && context.parentPos !== undefined && (
							<>
								<BlockActionsGroup asGroup editor={editor} pos={context.parentPos} />
								<GlassToolbarSeparator variant="inline" />
							</>
						)}
						<BlockActionsGroup editor={editor} pos={context.pos} />
					</>
				) : (
					<>
						{isInTable && tableGroup && (
							<>
								<TableMenuGroup buttons={tableGroup} editor={editor} onClick={closeHandler} />
								{tableGroup?.aggregation && (
									<TableAggregation disabled={!isCellSelection} editor={editor} />
								)}
								<GlassToolbarSeparator variant="inline" />
							</>
						)}
						<TextMenuGroup buttons={textGroup} editor={editor} isSelectionMenu />
						<ListMenuGroup buttons={listGroup} editor={editor} isSelectionMenu />
						{!isCellSelection && (
							<>
								<GlassToolbarSeparator variant="inline" />
								<InlineMenuGroup buttons={inlineGroup} editor={editor} onClick={closeHandler} />
							</>
						)}
					</>
				)}
			</GlassToolbar>
		</ComponentVariantProvider>
	);
});

export default InlineEditPanel;
